import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { WORKS } from "../../data/site";
import { Reveal, SectionMark } from "./Reveal";

const LAYOUT = [
    "col-span-12 md:col-span-7 aspect-[4/3]",
    "col-span-12 md:col-span-5 aspect-[4/3] md:aspect-[3/4] md:mt-28",
    "col-span-12 aspect-[16/8] md:-mt-14",
    "col-span-6 md:col-span-4 aspect-[3/4]",
    "col-span-6 md:col-span-4 aspect-[3/4] md:mt-24",
    "col-span-12 md:col-span-4 aspect-[4/3] md:-mt-10",
    "col-span-12 md:col-span-8 md:col-start-3 aspect-[16/9]",
    "col-span-12 md:col-span-6 md:col-start-4 aspect-[16/10]",
];

export const Work = () => {
    const [open, setOpen] = useState(null);
    const step = (d) => setOpen((i) => (i + d + WORKS.length) % WORKS.length);

    return (
        <section
            id="radovi"
            data-testid="work-section"
            className="relative px-5 py-24 md:px-10 md:py-40"
        >
            <div className="mx-auto max-w-[1400px]">
                <div className="flex items-end justify-between">
                    <div>
                        <Reveal>
                            <SectionMark>( 07 — Radovi )</SectionMark>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-6 pb-1 font-display text-[9vw] font-light leading-[1.06] tracking-tight text-bone md:text-[4vw]">
                                RADOVI
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2} className="hidden pb-3 md:block">
                        <p className="text-[11px] uppercase tracking-[0.34em] text-silver">
                            ( odabrani radovi )
                        </p>
                    </Reveal>
                </div>

                <div className="mt-12 grid grid-cols-12 gap-3 md:mt-20 md:gap-5">
                    {WORKS.map((w, i) => (
                        <Reveal key={w.src + i} delay={0.05 * (i % 3)} className={LAYOUT[i]}>
                            <button
                                data-testid={`work-item-${i}`}
                                onClick={() => setOpen(i)}
                                className="group relative block h-full w-full overflow-hidden"
                            >
                                <img
                                    src={w.src}
                                    alt={w.alt}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]"
                                />
                                <span className="absolute bottom-4 left-4 text-[10px] font-medium tracking-[0.3em] text-bone/0 transition-colors duration-500 group-hover:text-bone/90">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </button>
                        </Reveal>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {open !== null && (
                    <motion.div
                        data-testid="work-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => setOpen(null)}
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0a0a0b]/96 backdrop-blur-md"
                    >
                        <button
                            data-testid="lightbox-close"
                            aria-label="Zatvori"
                            onClick={() => setOpen(null)}
                            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center text-bone/80 transition-colors hover:text-bronze"
                        >
                            <X size={22} strokeWidth={1.5} />
                        </button>
                        <button
                            data-testid="lightbox-prev"
                            aria-label="Prethodna"
                            onClick={(e) => {
                                e.stopPropagation();
                                step(-1);
                            }}
                            className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center text-bone/70 transition-colors hover:text-bronze md:left-8"
                        >
                            <ChevronLeft size={26} strokeWidth={1.5} />
                        </button>
                        <motion.img
                            key={WORKS[open].src}
                            src={WORKS[open].src}
                            alt={WORKS[open].alt}
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[86vh] max-w-[92vw] object-contain"
                        />
                        <button
                            data-testid="lightbox-next"
                            aria-label="Sljedeća"
                            onClick={(e) => {
                                e.stopPropagation();
                                step(1);
                            }}
                            className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center text-bone/70 transition-colors hover:text-bronze md:right-8"
                        >
                            <ChevronRight size={26} strokeWidth={1.5} />
                        </button>
                        <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] text-silver">
                            {String(open + 1).padStart(2, "0")} /{" "}
                            {String(WORKS.length).padStart(2, "0")}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
